import React from 'react'
import Select, { components } from 'react-select'

import defaultTo from 'lodash/defaultTo'

import * as config from 'styles/tailwind.config'

import { UnfoldMore } from 'images/icons'
import { SingleValueProps } from 'react-select/src/components/SingleValue'

export interface IOption {
  value: string | number
  label: string
}

interface IProps {
  isSearchable?: boolean
  defaultValue?: string
  options: IOption[]
  plain?: boolean
  underline?: boolean
  center?: boolean
  placeholder?: string
  fixedSingleValue?: boolean
  label?: string
  required?: boolean
  error?: string
  menuWidth?: number
  value?: IOption | null
  isLoading?: boolean
  maxWidth?: number
  onChange: (option: IOption) => void
}

interface CustomSingleValueProps extends SingleValueProps<any> {
  fixedSingleValue?: boolean
  placeholder?: string
}

const DropdownIndicator = (props: any) => {
  return components.DropdownIndicator && <UnfoldMore danger={props.error ? true : false} />
}

const SingleValue = ({
  children,
  fixedSingleValue,
  placeholder,
  ...props
}: CustomSingleValueProps) => (
  <components.SingleValue {...props}>
    {fixedSingleValue ? placeholder : children}
  </components.SingleValue>
)

const SelectStyled: React.FC<IProps> = (props) => {
  const {
    isSearchable,
    defaultValue,
    options,
    plain,
    underline,
    center,
    placeholder,
    fixedSingleValue,
    label,
    required,
    menuWidth,
    onChange,
    isLoading,
    maxWidth,
    value,
    error,
  } = props

  const handleChange = (option: any) => {
    if (onChange) {
      onChange(option)
    }
  }

  // Calculates an estimation for the initial width based on character length of placeholder or the value
  const calcInitialWidth = () =>
    8 * (value ? value.label.length : placeholder ? placeholder.length : 0) + 70

  return (
    <div
      style={{ maxWidth: maxWidth, width: plain ? `${calcInitialWidth()}px` : '' }}
      className="w-full"
    >
      {label && (
        <label className={`label ${error && 'text-red'}`}>
          {label} {required && '*'}
        </label>
      )}
      <Select
        value={value ? value : undefined}
        defaultValue={options.find((option) => option.value === defaultValue)}
        options={options}
        onChange={handleChange}
        placeholder={placeholder || '–'}
        isSearchable={defaultTo(isSearchable, false)}
        isLoading={isLoading}
        theme={(theme) => ({
          ...theme,
          borderRadius: 0,
          colors: {
            ...theme.colors,
            primary25: config.theme.colors['gray-200'],
            primary50: config.theme.colors['gray-200'],
            primary75: config.theme.colors.brand.primary,
            primary: config.theme.colors.brand.primary,
            danger: config.theme.colors.red,
          },
        })}
        styles={{
          valueContainer: (base) => ({
            ...base,
            padding: 0,
          }),
          placeholder: (base) => {
            if (underline) {
              return {
                ...base,
                color: config.theme.colors.black,
                fontWeight: Number(config.theme.fontWeight['400']),
                letterSpacing: config.theme.letterSpacing['0.1'],
                whiteSpace: 'nowrap',
                fontSize: config.theme.fontSize['13'],
              }
            }
            return {
              ...base,
              color: config.theme.colors.black,
              fontWeight: plain
                ? Number(config.theme.fontWeight['400'])
                : Number(config.theme.fontWeight['500']),
              letterSpacing: plain
                ? config.theme.letterSpacing['0.23']
                : config.theme.letterSpacing['0.12'],
              whiteSpace: 'nowrap',
              fontSize: plain ? config.theme.fontSize['20'] : config.theme.fontSize['15'],
              margin: 0,
              right: center ? '50%' : base.right,
              transform: center ? 'translate(50%, -50%)' : base.transform,
            }
          },
          control: (base, state) => {
            if (underline) {
              return {
                ...base,
                borderWidth: config.theme.borderWidth['1'],
                borderColor: defaultValue
                  ? config.theme.colors.black
                  : config.theme.colors['gray-200'],
                borderRadius: 0,
                outline: state.isFocused && `2px solid ${config.theme.colors.brand.primary}`,
                boxShadow: state.isFocused
                  ? `0px 0px 5px  ${config.theme.colors.brand.primary}`
                  : '0',
                fontSize: config.theme.fontSize['13'],
                paddingLeft: 0,
                paddingTop: 0,
                paddingBottom: config.theme.spacing['10'],
                '&:hover': {
                  borderColor: config.theme.colors.black,
                },
                borderTop: 0,
                borderLeft: 0,
                borderRight: 0,
              }
            }

            if (plain) {
              return {
                ...base,
                minHeight: 30,
                backgroundColor: config.theme.colors.transparent,
                borderWidth: '0',
                borderColor: 'transparent',
                borderRadius: 0,
                outline: state.isFocused && `2px solid ${config.theme.colors.brand.primary}`,
                boxShadow: state.isFocused
                  ? `0px 0px 5px  ${config.theme.colors.brand.primary}`
                  : '0',
                fontSize: config.theme.fontSize['20'],
                paddingLeft: 0,
                paddingRight: 0,
                paddingTop: 0,
                paddingBottom: 0,
                '&:hover': {
                  borderColor: 'transparent',
                },
              }
            }

            return {
              ...base,
              backgroundColor: config.theme.colors.transparent,
              borderWidth: config.theme.borderWidth['1'],
              borderColor: error ? config.theme.colors.red : config.theme.colors['gray-200'],
              borderRadius: 0,
              outline: state.isFocused && `2px solid ${config.theme.colors.brand.primary}`,
              boxShadow: state.isFocused
                ? `0px 0px 5px  ${config.theme.colors.brand.primary}`
                : '0',
              fontSize: config.theme.fontSize['15'],
              paddingLeft: config.theme.spacing['19.5'],
              paddingRight: config.theme.spacing['1'],
              paddingTop: '15px',
              paddingBottom: '15px',
              '&:hover': {
                borderColor: error ? config.theme.colors.red : config.theme.colors['gray-200'],
              },
            }
          },
          menu: (base) => ({
            ...base,
            width: menuWidth ? menuWidth : base.width,
          }),
          singleValue: (base) => {
            if (underline) {
              return {
                ...base,
                position: 'relative',
                whiteSpace: 'normal',
                top: 'unset',
                transform: 'none',
              }
            }
            return {
              ...base,
              color: config.theme.colors.black,
              fontWeight: plain
                ? Number(config.theme.fontWeight['400'])
                : Number(config.theme.fontWeight['500']),
              letterSpacing: plain
                ? config.theme.letterSpacing['0.23']
                : config.theme.letterSpacing['0.12'],
              whiteSpace: 'nowrap',
              fontSize: plain ? config.theme.fontSize['20'] : config.theme.fontSize['15'],
              margin: 0,
              right: center ? '50%' : base.right,
              transform: center ? 'translate(50%, -50%)' : base.transform,
            }
          },
        }}
        components={{
          IndicatorSeparator: () => null,
          DropdownIndicator: () => DropdownIndicator(props),
          SingleValue: (props) => SingleValue({ fixedSingleValue, placeholder, ...props }),
        }}
      />
    </div>
  )
}

export default SelectStyled
